import { z } from 'zod';

export const deedsSchema = z.object({
    deedsOffice: z.string({ error: 'Deeds Office is required' }).min(1, 'Deeds Office is required'),
    searchType: z.enum(['Individual', 'Company', 'Erf', 'Title Deed'], { error: 'Search Type is required' }),

    // Individual
    idNo: z.string().optional().or(z.literal('')),
    firstName: z.string().optional().or(z.literal('')),
    surname: z.string().optional().or(z.literal('')),

    // Company
    reg1: z.string().length(4, "Reg1 must be 4 chars").optional().or(z.literal('')),
    reg2: z.string().length(6, "Reg2 must be 6 chars").optional().or(z.literal('')),
    reg3: z.string().length(2, "Reg3 must be 2 chars").optional().or(z.literal('')),
    businessName: z.string().optional().or(z.literal('')),

    // Erf
    townshipName: z.string().optional().or(z.literal('')),
    erfNo: z.string().regex(/^\d+$/, "Erf Number must be numeric").max(6, "Erf Number cannot exceed 6 digits").optional().or(z.literal('')),
    portionNo: z.string().optional().or(z.literal('')),

    // Title Deed
    titleDeedNo: z.string().optional().or(z.literal('')),
    bondAccountNo: z.string().optional().or(z.literal('')),

    // Common
    yourReference: z.string().optional().or(z.literal('')),
    voucherCode: z.string().optional().or(z.literal('')),
}).superRefine((data, ctx) => {
    if (data.searchType === 'Individual') {
        if (!data.idNo && (!data.firstName || !data.surname)) {
            if (!data.idNo) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "ID Number required (or Name+Surname)", path: ['idNo'] });
        }
    }
    if (data.searchType === 'Company') {
        const hasReg = data.reg1 && data.reg2 && data.reg3;
        if (!hasReg && !data.businessName) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Registration Number OR Business Name required", path: ['businessName'] });
        }
        if ((data.reg1 || data.reg2 || data.reg3) && (!data.reg1 || !data.reg2 || !data.reg3)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: "All 3 parts of Registration Number are required", path: ['reg2'] });
        }
    }
    if (data.searchType === 'Erf') {
        if (!data.townshipName) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Township Name is required", path: ['townshipName'] });
        if (!data.erfNo) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Erf Number is required", path: ['erfNo'] });
    }
    if (data.searchType === 'Title Deed') {
        if (!data.titleDeedNo) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Title Deed Number is required", path: ['titleDeedNo'] });
    }
});

export type DeedsFormData = z.infer<typeof deedsSchema>;

// ── Data contract types ────────────────────────────────────────────────────

export interface DeedsMatchItem {
    title_deed_no: string | null;
    erf_no: string | null;
    township: string | null;
    owner_name: string | null;
    property_description: string | null;
    enquiry_id: string;
    enquiry_result_id: string;
    reference: string | null;
}

export interface DeedsMatchResponse {
    success: boolean;
    message: string;
    log_id: string;
    matches: DeedsMatchItem[];
}

export interface DeedsMatchPayload {
    searchTypeInd: string;
    deedsOffice?: string;
    idNo?: string;
    firstName?: string;
    surname?: string;
    reg1?: string;
    reg2?: string;
    reg3?: string;
    businessName?: string;
    townshipName?: string;
    erfNo?: string;
    portionNo?: string;
    titleDeedNo?: string;
    bondAccno?: string;
    yourReference?: string;
    voucherCode?: string;
}

export interface DeedsResultPayload {
    enquiryId: number;
    enquiryResultId: number;
    searchTypeInd: string;
    yourReference?: string;
    voucherCode?: string;
}

export interface DeedsEnquiryResponse {
    message: string;
    success: boolean;
    log_id: string;
    report_data: any;
}

// ── Helpers ────────────────────────────────────────────────────────────────

/** Maps the UI searchType label to the XDS searchTypeInd token */
function resolveSearchTypeInd(formData: DeedsFormData): string {
    if (formData.searchType === 'Individual') {
        return formData.idNo ? 'IDNo' : 'PersonName';
    }
    if (formData.searchType === 'Company') {
        return (formData.reg1 || formData.reg2 || formData.reg3) ? 'RegNo' : 'BusinessName';
    }
    if (formData.searchType === 'Erf') return 'ErfNo';
    if (formData.searchType === 'Title Deed') return 'TitleDeedNo';
    return 'IDNo';
}

/**
 * Build the match payload, sending ONLY the fields relevant to the active search type.
 * This prevents stale values from a previous search type leaking into the request.
 */
export function buildMatchPayload(formData: DeedsFormData): DeedsMatchPayload {
    const searchTypeInd = resolveSearchTypeInd(formData);
    const common = {
        searchTypeInd,
        deedsOffice: formData.deedsOffice === 'ALL' ? undefined : formData.deedsOffice || undefined,
        yourReference: formData.yourReference || undefined,
        voucherCode: formData.voucherCode || undefined,
    };

    if (formData.searchType === 'Individual') {
        return {
            ...common,
            idNo: formData.idNo || undefined,
            firstName: formData.firstName || undefined,
            surname: formData.surname || undefined,
        };
    }
    if (formData.searchType === 'Company') {
        return {
            ...common,
            reg1: formData.reg1 || undefined,
            reg2: formData.reg2 || undefined,
            reg3: formData.reg3 || undefined,
            businessName: formData.businessName || undefined,
        };
    }
    if (formData.searchType === 'Erf') {
        return {
            ...common,
            townshipName: formData.townshipName || undefined,
            erfNo: formData.erfNo || undefined,
            portionNo: formData.portionNo || undefined,
        };
    }
    if (formData.searchType === 'Title Deed') {
        return {
            ...common,
            titleDeedNo: formData.titleDeedNo || undefined,
            bondAccno: formData.bondAccountNo || undefined,
        };
    }
    return common;
}

// ── API calls ──────────────────────────────────────────────────────────────

const BASE_URL = '/verification/deeds-enquiry';

export const deedsEnquiryService = {
    getMatch: async (payload: DeedsMatchPayload): Promise<DeedsMatchResponse> => {
        const axios = (await import('../axiosConfig')).default;
        const response = await axios.post<DeedsMatchResponse>(`${BASE_URL}/match`, payload);
        return response.data;
    },

    getResult: async (payload: DeedsResultPayload): Promise<DeedsEnquiryResponse> => {
        const axios = (await import('../axiosConfig')).default;
        const response = await axios.post<DeedsEnquiryResponse>(`${BASE_URL}/result`, payload);
        return response.data;
    },
};
